if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.bindCustomerProofPreviewProperties();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getSubmitButtonLabel(button) {
        const directSpan = button.querySelector(':scope > span:not(.sold-out-message)');
        if (directSpan) return directSpan.textContent.trim();
        return button.textContent.trim();
      }

      setSubmitButtonLabel(button, label) {
        const directSpan = button.querySelector(':scope > span:not(.sold-out-message)');
        if (directSpan) directSpan.textContent = label;
      }

      setSubmissionLoading(isLoading, label = '', { lock = true } = {}) {
        const button = this.submitButton;
        if (!button) return;
        if (isLoading) {
          if (!button.dataset.dcSubmitIdleLabel) {
            button.dataset.dcSubmitIdleLabel = this.getSubmitButtonLabel(button);
          }
          if (label) this.setSubmitButtonLabel(button, label);
          button.classList.add('loading');
          button.setAttribute('aria-busy', 'true');
          if (lock) button.setAttribute('aria-disabled', 'true');
          button.querySelector('.loading__spinner')?.classList.remove('hidden');
          return;
        }

        if (button.dataset.dcSubmitIdleLabel) {
          this.setSubmitButtonLabel(button, button.dataset.dcSubmitIdleLabel);
          delete button.dataset.dcSubmitIdleLabel;
        }
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.syncCustomerProofPreviewProperties();
        const rawFormData = new FormData(this.form);
        const submittedVariantId = rawFormData.get('id');
        const cartAddPayload = this.normalizeCartAddPayload(rawFormData);
        if (this.cart) {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);

          if (cartAddPayload.items) {
            cartAddPayload.sections = sections;
            cartAddPayload.sections_url = window.location.pathname;
          } else {
            cartAddPayload.formData.append('sections', sections);
            cartAddPayload.formData.append('sections_url', window.location.pathname);
          }

          this.cart.setActiveElement(document.activeElement);
        }

        if (cartAddPayload.items) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(cartAddPayload);
        } else {
          config.body = cartAddPayload.formData;
        }

        window.StorefrontRequest.json(`${routes.cart_add_url}`, config, { timeoutMs: 30000 })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: submittedVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (this.cartEditCoordinator?.isCartEditActive?.()) {
              await this.cartEditCoordinator.completeCartReplacement(response);
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const cartNotificationState = this.getCartNotificationState(response, submittedVariantId);
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: submittedVariantId,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(cartNotificationState);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(cartNotificationState);
            }
          })
          .catch(() => {
            // Shopify may have accepted the add even if its response was lost.
            // Check the authoritative cart; never automatically repeat the add.
            window.location.assign(window.routes.cart_url);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.livePreviewV3Coordinator?.releaseSubmissionUi?.();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      bindCustomerProofPreviewProperties() {
        const formId = this.form.getAttribute('id');
        const proofPreviewCheckbox = document.querySelector(
          `[data-proof-preview-toggle][data-proof-preview-form="${formId}"]`
        );

        if (!proofPreviewCheckbox) return;

        proofPreviewCheckbox.addEventListener('change', () => this.syncCustomerProofPreviewProperties());
        this.syncCustomerProofPreviewProperties();
      }

      syncCustomerProofPreviewProperties() {
        const formId = this.form.getAttribute('id');
        const proofPreviewCheckbox = document.querySelector(
          `[data-proof-preview-toggle][data-proof-preview-form="${formId}"]`
        );
        const blockId = proofPreviewCheckbox?.getAttribute('data-proof-preview-toggle');

        if (!blockId) return;

        this.form.querySelectorAll(`[data-proof-preview-form-input="${blockId}"]`).forEach((input) => {
          input.remove();
        });

        if (!proofPreviewCheckbox.checked) return;

        const addProofInput = (name, value) => {
          const input = document.createElement('input');
          input.setAttribute('type', 'hidden');
          input.setAttribute('name', name);
          input.setAttribute('data-proof-preview-form-input', blockId);
          input.value = value;
          this.form.append(input);
        };

        addProofInput('properties[Request 3D Proof]', 'Yes');
        addProofInput('properties[_customer_proof_requested]', '1');
      }

      normalizeCartAddPayload(formData) {
        const entries = Array.from(formData.entries());
        const hasNestedItems = entries.some(([name]) => name.startsWith('items['));

        if (!hasNestedItems) return { formData };

        const items = [];
        const extraItemsByKey = new Map();
        const mainItem = {
          id: formData.get('id'),
          quantity: formData.get('quantity') || '1',
          properties: {},
        };

        entries.forEach(([name, value]) => {
          const propertyMatch = name.match(/^properties\[([^\]]+)\]$/);
          if (propertyMatch) {
            mainItem.properties[propertyMatch[1]] = value;
            return;
          }

          const itemPropertyMatch = name.match(/^items\[([^\]]+)\]\[properties\]\[([^\]]+)\]$/);
          if (itemPropertyMatch) {
            this.getNormalizedCartItem(extraItemsByKey, itemPropertyMatch[1]).properties[itemPropertyMatch[2]] = value;
            return;
          }

          const itemFieldMatch = name.match(/^items\[([^\]]+)\]\[(id|quantity|selling_plan)\]$/);
          if (itemFieldMatch) {
            this.getNormalizedCartItem(extraItemsByKey, itemFieldMatch[1])[itemFieldMatch[2]] = value;
          }
        });

        if (mainItem.id) items.push(mainItem);
        extraItemsByKey.forEach((item) => {
          if (item.id && Number(item.quantity || 0) > 0) items.push(item);
        });

        return { items };
      }

      getCartNotificationState(response, submittedVariantId) {
        if (!Array.isArray(response?.items)) return response;

        const selectedItem =
          response.items.find((item) => String(item.id) === String(submittedVariantId)) || response.items[0];

        return {
          ...selectedItem,
          sections: response.sections || {},
        };
      }

      getNormalizedCartItem(itemsByKey, key) {
        if (!itemsByKey.has(key)) {
          itemsByKey.set(key, { id: null, quantity: '1', selling_plan: null, properties: {} });
        }

        return itemsByKey.get(key);
      }
    }
  );
}
